import { DaVinciSelection } from "../utils/DaVinciSelection.js";

/**
 * DaVinci Floating Toolbar
 * 
 * Provides "Medium-like" floating controls for Rich Text Editing.
 * Appears when user selects text in a .rich-text block.
 */
export class DaVinciToolbar {
    constructor() {
        this.el = null;
        this.visible = false;
        this.currentRange = null;
        this.init();
    }

    init() {
        this.createDOM();
        this.bindEvents();
    }

    createDOM() {
        const div = document.createElement('div');
        div.className = 'davinci-floating-toolbar hidden';
        div.innerHTML = `
            <div class="dv-toolbar-actions">
                <button type="button" data-cmd="bold" class="dv-tool-btn" title="${window.locale.t('studio.bold')}">
                    <span class="material-symbols-outlined">format_bold</span>
                </button>
                <button type="button" data-cmd="italic" class="dv-tool-btn" title="${window.locale.t('studio.italic')}">
                    <span class="material-symbols-outlined">format_italic</span>
                </button>
                <div class="dv-tool-separator"></div>
                <button type="button" data-cmd="foreColor" data-val="#ff5252" class="dv-tool-btn text-red-500" title="${window.locale.t('studio.highlight_red')}">
                    <span class="material-symbols-outlined">format_color_text</span>
                </button>
                 <button type="button" data-cmd="foreColor" data-val="#448aff" class="dv-tool-btn text-blue-500" title="${window.locale.t('studio.highlight_blue')}">
                    <span class="material-symbols-outlined">format_color_text</span>
                </button>
            </div>
            <div class="dv-toolbar-arrow"></div>
        `;
        document.body.appendChild(div);
        this.el = div;
    }

    bindEvents() {
        // Global Selection Listener
        document.addEventListener('selectionchange', () => this.onSelectionChange());

        // Toolbar Button Clicks
        this.el.addEventListener('mousedown', (e) => {
            // Prevent loss of focus from editor
            e.preventDefault();
        });

        this.el.addEventListener('click', (e) => {
            const btn = e.target.closest('button');
            if (!btn) return;

            const command = btn.dataset.cmd;
            const value = btn.dataset.val;

            this.execute(command, value);
        });
    }

    onSelectionChange() {
        const selection = window.getSelection();

        // 1. Valid Check: Is text selected?
        if (selection.isCollapsed) {
            this.hide();
            return;
        }

        // 2. Scope Check: Is it inside a DaVinci Editor Block?
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const editorBlock = (container.nodeType === 3 ? container.parentNode : container).closest('.dv-text-block.rich-text');

        if (!editorBlock) {
            this.hide();
            return;
        }

        // 3. Update Position
        this.currentRange = range;
        this.show(range);
    }

    show(range) {
        const rect = range.getBoundingClientRect();

        // Calculate Position (Centered above selection)
        const toolbarRect = this.el.getBoundingClientRect();
        const top = rect.top + window.scrollY - toolbarRect.height - 10; // 10px spacing
        const left = rect.left + window.scrollX + (rect.width / 2) - (toolbarRect.width / 2);

        this.el.style.top = `${top}px`;
        this.el.style.left = `${left}px`;

        if (!this.visible) {
            this.el.classList.remove('hidden');
            this.el.classList.add('visible');
            this.visible = true;
        }
    }

    hide() {
        if (this.visible) {
            this.el.classList.remove('visible');
            this.el.classList.add('hidden');
            this.visible = false;
        }
    }

    execute(command, value) {
        // Ensure the editor still has focus or restore range
        // Since we blocked mousedown default, focus should be conserved.
        DaVinciSelection.applyStyle(command, value);

        // Update Button State? (Optional)
        // Trigger Input Event on the editor to save state?
        // execCommand fires 'input' event on contenteditable automatically in modern browsers.
    }
}

// Singleton Instance
export const daVinciToolbar = new DaVinciToolbar();
